import type { ACL, Persisted, Profile, Rider } from '@tunasong/schemas'
import { getProfileGear } from './profile-gear.js'
import { isDrifted } from './provenance.js'
import { hasStageSetup } from './stage-setup.js'

/**
 * Band readiness for the rider (docs/ux/2026-10-16-rider-collaboration-ux.md, "Band page: readiness card").
 * Pure: the card and the rider's preview bar render what this returns, and render nothing when it is unavailable.
 */

/** What the viewer is asked to do, if anything */
export type ViewerState =
  /** No stage setup yet: "Add your stage setup…" */
  | 'missing'
  /** Their setup changed after the latest rider took a copy of it */
  | 'drifted'
  /** They have a setup, but the latest rider has no row for them (joined after it was made) */
  | 'not-on-rider'
  /** Nothing to do */
  | 'ready'
  /** Said "I don't perform with {Band}": only the way back is offered */
  | 'not-performing'
  /** Not a member of the band (an admin or a link viewer): no personal state */
  | 'not-member'

export type Readiness =
  | { status: 'unavailable' }
  | {
      status: 'ready'
      /** Members who perform with the band, in member order */
      performers: string[]
      /** Performers without a stage setup */
      missing: string[]
      viewer: ViewerState
      /** The viewer's setup date, for the drift line */
      viewerUpdatedAt?: string
      /** The band's latest rider: the one readiness and drift are measured against */
      rider: Persisted<Rider>
    }

export interface ReadinessInput {
  memberIds: string[]
  /** Undefined while loading or after a failed load */
  profiles?: Profile[]
  /** Undefined while loading or after a failed load */
  notPerforming?: string[]
  viewerId?: string
  riders: Persisted<Rider>[]
}

const groupPrincipals = new Set(['PUBLIC', 'AUTHENTICATED'])

/** A band's members: its owner and every named principal on its ACL, once each (as the server's `bandMemberIds`) */
export const bandMemberIds = (band: { userId?: string; acls?: ACL[] }): string[] => {
  const principals = (band.acls ?? []).map(acl => acl.principal).filter(principal => !groupPrincipals.has(principal))
  return [...new Set([band.userId, ...principals].filter((id): id is string => Boolean(id)))]
}

/** How a member is named to bandmates: their name, else their email's local part. Never a user id. */
export const displayName = (profile?: Pick<Profile, 'userId'> & Partial<Pick<Profile, 'name' | 'email'>>) =>
  profile?.name?.trim() || profile?.email?.split('@')[0]?.trim() || 'A bandmate'

const latestRider = (riders: Persisted<Rider>[]) =>
  [...riders].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0))[0]

export const getReadiness = ({ memberIds, profiles, notPerforming, viewerId, riders }: ReadinessInput): Readiness => {
  const rider = latestRider(riders)
  if (!profiles || !notPerforming || !rider) {
    return { status: 'unavailable' }
  }
  const optedOut = new Set(notPerforming)
  const byUser = new Map(profiles.map(profile => [profile.userId, profile]))
  const performers = memberIds.filter(id => !optedOut.has(id))
  const missing = performers.filter(id => !hasStageSetup(getProfileGear(byUser.get(id) ?? ({} as Profile))))

  const viewerGear = viewerId ? getProfileGear(byUser.get(viewerId) ?? ({} as Profile)) : undefined
  const viewerUpdatedAt = viewerGear?.updatedAt
  const row = rider.members?.find(member => member.userId === viewerId)

  const viewer = ((): ViewerState => {
    if (!viewerId || !memberIds.includes(viewerId)) {
      return 'not-member'
    }
    if (optedOut.has(viewerId)) {
      return 'not-performing'
    }
    if (!hasStageSetup(viewerGear)) {
      return 'missing'
    }
    if (!row) {
      return 'not-on-rider'
    }
    return isDrifted(row, viewerGear, rider.updatedAt) ? 'drifted' : 'ready'
  })()

  return {
    status: 'ready',
    performers,
    missing,
    viewer,
    ...(viewerUpdatedAt ? { viewerUpdatedAt } : {}),
    rider,
  }
}
